import React, { useState } from 'react';
import { MoveButton, IconWrapper, Wrapper, NodeWrapper } from './RotationTree.styles';
import { useRotationStore } from '../../context/RotationContext';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { useEventStore } from '../../context/EventContext';
import { useEventTypeStore } from '../../context/EventTypeContext';
import { Rotation } from '../../types/ViewModel/Rotation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import { PortCall } from '../../types/ViewModel/PortCall';
import { BerthCall } from '../../types/ViewModel/BerthCall';
import { CargoOperation } from '../../types/ViewModel/CargoOperation';
import usePostTankersEvent from '../../api/usePostTankersEvent';
import { useVesselStore } from '../../context/VesselContext';
import useRotation from '../../app/useRotation';
import MoveEventsPopup from './move-events-popup/MoveEventsPopup';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useAuthStore } from '../../context/AuthContext';
import { useSWService } from '../../api/sw/useSWService';
import useApiEndpoints from '../../context/useApiEndpoints';

type Props = {
    isHistoric?: boolean;
    rotationTree: Rotation;
};

const RotationTreeComponent: React.FC<Props> = (props) => {
    const [moveFromCargoOp, setMoveFromCargoOp] = useState<CargoOperation | null>(null);
    const [moveFromBerthCall, setMoveFromBerthCall] = useState<BerthCall | null>(null);
    const [moveFromPortCall, setMoveFromPortCall] = useState<PortCall | null>(null);
    const [openedRotationNodeIndex, setOpenedRotationNodeIndex] = useState<number | null>(-2);

    const { selectedVessel } = useVesselStore();
    const { setSelectedEventType } = useEventTypeStore();
    const { selectedNode, updatedRotation } = useRotationStore();
    const { getParentBerthForMovedToCargoOpSamePortCall, getBerthCall, moveToPortCallItem } = useRotation();
    const { fullEventArray, setSelectedEvent } = useEventStore();
    const { moveEventsToCargoOperation, moveEventsToBerthCall, moveEventsToPortCall } = usePostTankersEvent();
    const { setQueryParamPortCall, setQueryParamBerthCall, setQueryParamCargoOperation } = queryParamsUtils();
    const { role } = useAuthStore();
    const { refreshRequestsContaining } = useSWService();
    const { apiEndpoints } = useApiEndpoints();

    // =============  Use Effects ================

    const onSelectedPortNode = (node: IRotationNode | null, portIndex: number) => (event) => {
        event.stopPropagation();
        if (node && node.id && node.id > 0 && portIndex >= 0) {
            if (portIndex !== openedRotationNodeIndex) {
                setOpenedRotationNodeIndex(portIndex);
            } else {
                setOpenedRotationNodeIndex(-1);
            }
            setSelectedEvent(null);
            setSelectedEventType(null);
            setMoveOnNode(node);
            setQueryParamPortCall(node.id.toString(), node.reportLocation);
        }
    };

    const onSelectedRotationNode = (node: IRotationNode | null) => (event) => {
        event.stopPropagation();
        // if (node && node.id && node.id > 0) {
        if (node && node.id) {
            setSelectedEvent(null);
            setSelectedEventType(null);
            setMoveOnNode(node);
            switch (node.type) {
                case RotationTreeEnum.CargoOperation:
                    setQueryParamCargoOperation(node);
                    break;
                case RotationTreeEnum.BerthCall:
                    setQueryParamBerthCall(node);
                    break;
            }
        }
    };

    const setMoveOnNode = (node: IRotationNode) => {
        switch (node.type) {
            case RotationTreeEnum.CargoOperation: {
                const co = node as CargoOperation;
                if (
                    moveFromCargoOp &&
                    moveFromCargoOp.berthCall &&
                    co.berthCall &&
                    (moveFromCargoOp.id !== co.id || moveFromCargoOp.berthCall.id !== co.berthCall.id)
                ) {
                    setMoveFromCargoOp(null);
                }
                setMoveFromBerthCall(null);
                setMoveFromPortCall(null);
                break;
            }
            case RotationTreeEnum.BerthCall: {
                const bc = node as BerthCall;
                if (
                    moveFromBerthCall &&
                    moveFromBerthCall.portCall &&
                    bc.portCall &&
                    (moveFromBerthCall.id !== bc.id || moveFromBerthCall.portCall.id !== bc.portCall.id)
                ) {
                    setMoveFromBerthCall(null);
                }
                setMoveFromCargoOp(null);
                setMoveFromPortCall(null);
                break;
            }
            case RotationTreeEnum.PortCall: {
                const pc = node as PortCall;
                if (moveFromPortCall && moveFromPortCall.id !== pc.id) {
                    setMoveFromPortCall(null);
                }
                setMoveFromBerthCall(null);
                setMoveFromCargoOp(null);
                break;
            }
            case RotationTreeEnum.Sea:
                setMoveFromPortCall(null);
                setMoveFromBerthCall(null);
                setMoveFromCargoOp(null);
                break;
        }
    };

    const nodeStatusTitleStr = (node: IRotationNode, initialStr: string): string | undefined => {
        let str = initialStr;
        if (node.timezoneWarningText) {
            str = str + '. ' + node.timezoneWarningText;
        }
        if (node.delayInputWarningText) {
            str = str + '. ' + node.delayInputWarningText;
        }
        return str;
    };

    const createCompletedIcon = (node: IRotationNode, initialStr: string) => {
        return (
            <span title={nodeStatusTitleStr(node, initialStr)} className={`edit-icon completed-rotation-item`}>
                <FontAwesomeIcon className="status-icon status-approved" icon="check-circle" />
            </span>
        );
    };

    const createEditIcon = (node: IRotationNode, initialStr: string) => {
        return (
            <span
                title={nodeStatusTitleStr(node, initialStr)}
                className={`edit-icon edited-rotation-item`}
                dangerouslySetInnerHTML={{
                    __html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100%" height="100%"> <circle cx="50" cy="50" r="50" stroke-width="1" fill="OrangeRed"/> <path d="M0,44.439791L18.98951,54.569246 0.47998798,62.66881z M17.428029,12.359973L36.955557,23.568769 21.957478,49.686174 20.847757,46.346189 15.11851,45.756407 14.138656,42.166935 8.5292659,41.966761 6.9493899,38.037481 2.4399572,38.477377z M26.812517,0.0009765625C27.350616,-0.012230873,27.875986,0.10826397,28.348372,0.3782568L42.175028,8.3180408C43.85462,9.2780154,44.234529,11.777948,43.02482,13.89789L41.375219,16.767812 21.460039,5.3381228 23.10964,2.4582005C23.979116,0.941679,25.437378,0.034730911,26.812517,0.0009765625z" fill="white" transform="translate(30 20)"/></svg>',
                }}
            />
        );
    };

    const getEditCompletedIcon = (node: IRotationNode) => {
        let initialStr = 'All mandatory events registered';
        if (node.nodeStatus === 'edited') initialStr = 'There are still events to be registered';
        if (node.delayInputWarningText) {
            return createEditIcon(node, initialStr);
        } else if (node.timezoneWarningText) {
            return createEditIcon(node, initialStr);
        } else if (node.nodeStatus === 'completed') {
            return createCompletedIcon(node, initialStr);
        } else if (node.nodeStatus === 'edited') {
            return createEditIcon(node, initialStr);
        }
    };

    const getMoveIcon = (node: IRotationNode, onMove: (node: IRotationNode) => void) => {
        if (node.actionMsg.displayMoveIcon) {
            if (hasReaderPermissionOnly()) {
                return (
                    <MoveButton className="is-moved-to" isSelected={true}>
                        <span>
                            <FontAwesomeIcon title={getMoveTooltipText(node.actionMsg.toolTipText)} icon="arrow-circle-right" />
                        </span>
                    </MoveButton>
                );
            } else {
                return (
                    <MoveButton className="is-moved-to" isSelected={true} onClick={() => onMove(node)}>
                        <span>
                            <FontAwesomeIcon title={getMoveTooltipText(node.actionMsg.toolTipText)} icon="arrow-circle-right" />
                        </span>
                    </MoveButton>
                );
            }
        }
        return null;
    };

    const getMovedToIcon = (node: IRotationNode) => {
        if (node.isMovedTo) {
            return (
                <MoveButton className="is-moved-to" isSelected={true}>
                    <span>
                        <FontAwesomeIcon title={getMoveTooltipText(node.actionMsg.toolTipText)} icon="arrow-circle-left" />
                    </span>
                </MoveButton>
            );
        }

        return null;
    };

    const getPortClusterIcon = (portCallItem: PortCall) => {
        if (portCallItem.isInPortCluster && portCallItem.reportLocation === ReportLocationEnum.Port) {
            return (
                <span className="is-in-port-cluster">
                    <FontAwesomeIcon title="Port cluster" icon="project-diagram" />
                </span>
            );
        }

        return null;
    };

    const getMoveTooltipText = (txt: string | null): string | undefined => {
        return txt ?? undefined;
    };

    const isRotationIdEqualCss = (
        node: IRotationNode | null,
        pcId: number,
        bcId: number | null,
        coId: number | null,
        reportLocation: ReportLocationEnum | null
    ): string => {
        let isEqual = false;
        if (node) {
            switch (node.type) {
                case RotationTreeEnum.Sea:
                case RotationTreeEnum.PortCall: {
                    const pc = node as PortCall;
                    isEqual = pc.id === pcId && !bcId && !coId && pc.reportLocation === reportLocation;
                    break;
                }
                case RotationTreeEnum.BerthCall: {
                    const bc = node as BerthCall;
                    if (bc.portCall && bcId) {
                        isEqual = bc.portCall.id === pcId && bc.id === bcId && !coId;
                    }
                    break;
                }
                case RotationTreeEnum.CargoOperation: {
                    const co = node as CargoOperation;
                    if (co.berthCall && co.berthCall.portCall && bcId && coId) {
                        isEqual = co.berthCall.portCall.id === pcId && co.berthCall.id === bcId && co.id === coId;
                    }
                    break;
                }
            }
        }
        return isEqual ? 'is-selected' : '';
    };

    const getMovedToOnClickCss = (node: IRotationNode | null): string => {
        return node && node.movedFromClicked ? 'is-moved-to-node' : '';
    };

    const getMovedToCss = (node: IRotationNode | null): string => {
        return node && node.isMovedTo ? 'is-moved-to' : '';
    };

    const isManualBerth = (berthCall: BerthCall | null): string => {
        return berthCall?.berthCallId < 0 ? 'is-manual-berth' : '';
    };

    const onShowMoveCargoPopup = (co: CargoOperation) => {
        if (moveFromCargoOp) {
            if (moveFromCargoOp.id === co.id) {
                setMoveFromCargoOp(null);
            } else {
                setMoveFromCargoOp(co);
            }
        } else {
            setMoveFromCargoOp(co);
        }
    };

    const onShowMoveBerthCallPopup = (bc: BerthCall) => {
        if (moveFromBerthCall) {
            if (moveFromBerthCall.id === bc.id) {
                setMoveFromBerthCall(null);
            } else {
                setMoveFromBerthCall(bc);
            }
        } else {
            setMoveFromBerthCall(bc);
        }
    };

    const onShowMovePortCallPopup = (pc: PortCall) => {
        if (moveFromPortCall) {
            if (moveFromPortCall.id === pc.id) {
                setMoveFromPortCall(null);
            } else {
                setMoveFromPortCall(pc);
            }
        } else {
            setMoveFromPortCall(pc);
        }
    };

    const onCargoMoveBtn = () => {
        if (moveFromCargoOp) {
            const movedToBerthCall = getParentBerthForMovedToCargoOpSamePortCall(moveFromCargoOp);
            if (movedToBerthCall) {
                const moveEventsResult = moveEventsToCargoOperation(
                    selectedVessel?.imoNumber,
                    moveFromCargoOp.id,
                    moveFromCargoOp.portCall?.id,
                    moveFromCargoOp.berthCall?.id,
                    movedToBerthCall.id,
                    movedToBerthCall.berthId
                );
                setMoveFromCargoOp(null);
                moveEventsResult.then((response) => {
                    if (response) {
                        const voyageNumbers = [];
                        if (moveFromCargoOp?.berthCall?.voyageNumber) voyageNumbers.push(moveFromCargoOp?.berthCall?.voyageNumber);
                        const movedToVoyNr = movedToBerthCall?.voyageNumber;
                        if (movedToVoyNr && voyageNumbers?.[0] !== movedToVoyNr) voyageNumbers.push(movedToVoyNr);

                        voyageNumbers.forEach((v) => {
                            const params = '?voyageNumber1=' + v + '&voyageNumber2=&imoNumber=' + selectedVessel.imoNumber;
                            refreshRequestsContaining([apiEndpoints['getTankersEventDataDeltaForVoyage'].url], params);
                        });
                    }
                });
            }
        }
    };

    const onBerthCallMoveBtn = (node: IRotationNode) => {
        if (moveFromBerthCall) {
            const bc = node as BerthCall;
            const movedToBerthCall = getBerthCall(props.rotationTree, moveFromBerthCall.portCall?.id, bc.actionMsg.id);
            if (movedToBerthCall) {
                const moveEventsResult = moveEventsToBerthCall(
                    selectedVessel?.imoNumber,
                    moveFromBerthCall.portCall?.id,
                    moveFromBerthCall.id,
                    movedToBerthCall.id,
                    movedToBerthCall.berthId,
                    movedToBerthCall.manualBerthSequenceNumber
                );
                setMoveFromBerthCall(null);
                moveEventsResult.then((response) => {
                    if (response) {
                        const voyageNumbers = [];
                        if (moveFromBerthCall?.voyageNumber) voyageNumbers.push(moveFromBerthCall?.voyageNumber);
                        const movedToVoyNr = movedToBerthCall?.voyageNumber;
                        if (movedToVoyNr && voyageNumbers?.[0] !== movedToVoyNr) voyageNumbers.push(movedToVoyNr);

                        voyageNumbers.forEach((v) => {
                            const params = '?voyageNumber1=' + v + '&voyageNumber2=&imoNumber=' + selectedVessel.imoNumber;
                            refreshRequestsContaining([apiEndpoints['getTankersEventDataDeltaForVoyage'].url], params);
                        });
                    }
                });
            }
        }
    };

    const getDeletedMovedIcon = (node: IRotationNode) => {
        if (node.isMoved || node.isDeleted) {
            let nodeStr = '';
            switch (node.type) {
                case RotationTreeEnum.PortCall:
                    nodeStr = 'port call';
                    break;
                case RotationTreeEnum.BerthCall:
                    nodeStr = 'berth call';
                    break;
                case RotationTreeEnum.CargoOperation:
                    nodeStr = 'cargo operation';
                    break;
            }
            if (node.isMoved) {
                const title = 'This ' + nodeStr + ' is moved';
                return <FontAwesomeIcon className="is-moved-icon" title={node.deletedIconTooltipText ?? title} icon="info-circle" />;
            } else if (node.isDeleted) {
                const title = 'This ' + nodeStr + ' is deleted';
                return <FontAwesomeIcon className="is-deleted-icon" title={node.deletedIconTooltipText ?? title} icon="info-circle" />;
            }
        }
        return null;
    };

    const onPortCallMoveBtn = () => {
        if (moveFromPortCall) {
            const moveEventsPortCall = moveToPortCallItem(updatedRotation?.portCallArray, fullEventArray, moveFromPortCall);
            if (moveEventsPortCall) {
                const moveEventsResult = moveEventsToPortCall(selectedVessel?.imoNumber, moveEventsPortCall);
                moveEventsResult.then((response) => {
                    if (response) {
                        const voyageNumbers = [];
                        if (moveFromPortCall?.voyageNumber1) voyageNumbers.push(moveFromPortCall?.voyageNumber1);
                        const movedToVoyNr = moveEventsPortCall.toVoyageNumber;
                        if (movedToVoyNr && voyageNumbers?.[0] !== movedToVoyNr) voyageNumbers.push(movedToVoyNr);

                        voyageNumbers.forEach((v) => {
                            const params = '?voyageNumber1=' + v + '&voyageNumber2=&imoNumber=' + selectedVessel.imoNumber;
                            refreshRequestsContaining([apiEndpoints['getTankersEventDataDeltaForVoyage'].url], params);
                        });
                    }
                });
            }
        }
    };

    const onCancelCargoMoveBtn = () => {
        setMoveFromCargoOp(null);
    };

    const onOkCargoMoveBtn = () => {
        setMoveFromCargoOp(null);
    };

    const isMoveCargoPopupOpen = (cargoOp: CargoOperation): boolean => {
        if (moveFromCargoOp) {
            return moveFromCargoOp.id === cargoOp.id && moveFromCargoOp.berthCall?.id === cargoOp.berthCall?.id;
        }
        return false;
    };

    const onCancelBerthCallMoveBtn = () => {
        setMoveFromBerthCall(null);
    };

    const onOkBerthCallMoveBtn = () => {
        setMoveFromBerthCall(null);
    };

    const isMoveBerthCallPopupOpen = (berthCall: BerthCall): boolean => {
        if (moveFromBerthCall) {
            return moveFromBerthCall.id === berthCall.id && moveFromBerthCall.portCall?.id === berthCall.portCall?.id;
        }
        return false;
    };

    const onCancelPortCallMoveBtn = () => {
        setMoveFromPortCall(null);
    };

    const onOkPortCallMoveBtn = () => {
        setMoveFromPortCall(null);
    };

    const isMovePortCallPopupOpen = (portCall: PortCall): boolean => {
        if (moveFromPortCall) {
            return moveFromPortCall.id === portCall.id;
        }
        return false;
    };

    const openedPortNode = (portCallItem: PortCall): boolean => {
        if (portCallItem && selectedNode) {
            switch (selectedNode.type) {
                case RotationTreeEnum.Sea:
                case RotationTreeEnum.PortCall: {
                    const pc = selectedNode as PortCall;
                    return (
                        portCallItem.id === pc.id &&
                        portCallItem.reportLocation === pc.reportLocation &&
                        openedRotationNodeIndex !== -1 &&
                        selectedNode.reportLocation === ReportLocationEnum.Port
                    );
                }
                case RotationTreeEnum.BerthCall: {
                    const bc = selectedNode as BerthCall;
                    return (
                        portCallItem.id === bc.portCall?.id &&
                        portCallItem.reportLocation === bc.portCall?.reportLocation &&
                        openedRotationNodeIndex !== -1 &&
                        selectedNode.reportLocation === ReportLocationEnum.Port
                    );
                }
                case RotationTreeEnum.CargoOperation: {
                    const co = selectedNode as CargoOperation;
                    return (
                        portCallItem.id === co.berthCall?.portCall?.id &&
                        portCallItem.reportLocation === co.berthCall?.portCall?.reportLocation &&
                        openedRotationNodeIndex !== -1 &&
                        selectedNode.reportLocation === ReportLocationEnum.Port
                    );
                }
            }
        }
        return false;
    };

    const hasReaderPermissionOnly = () => {
        return role && !role.isWriter;
    };

    const getBerthCallName = (bc: BerthCall): string => {
        if (bc.name) {
            return bc.name;
        }
        return '???';
        // return '??? (berth-id: ' + bc.berthId.toString() + ')';
    };

    const getBcNumber = (bc: BerthCall): number | null => {
        if (bc) return bc.berthCallId;
        return null;
    };

    const getCoNumber = (co: CargoOperation): number | null => {
        if (co) return co.cargoOperationId;
        return null;
    };

    const getCargoOpName = (cargoOp: CargoOperation): string => {
        return cargoOp.name;
    };

    const createRotation = () => {
        return (
            <Wrapper>
                <div className="rotation-tree-list">
                    {props.rotationTree &&
                        props.rotationTree.portCallArray &&
                        props.rotationTree.portCallArray.length > 0 &&
                        props.rotationTree.portCallArray.map((portCallItem, portIndex) => {
                            return (
                                <NodeWrapper
                                    className={`rotation-parent-node ${openedPortNode(portCallItem) ? 'opened-tree-node' : 'closed-tree-node'}`}
                                    key={`p-` + portCallItem.portCallId + `-` + portCallItem.reportLocation}
                                >
                                    <div
                                        className={`rotation-node port-node 
                                ${isRotationIdEqualCss(selectedNode, portCallItem.id, null, null, portCallItem.reportLocation)}
                                ${portCallItem?.reportLocation === ReportLocationEnum.Sea ? 'is-at-sea' : ''}
                                `}
                                        onClick={onSelectedPortNode(portCallItem, portIndex)}
                                    >
                                        <span className="rotation-name">
                                            <span>{portCallItem.voyageNumberPresentationString}</span>
                                            <span className="sep"> - </span>
                                            {portCallItem.name}
                                            {getPortClusterIcon(portCallItem)}

                                            <IconWrapper>
                                                {getDeletedMovedIcon(portCallItem)}
                                                {getEditCompletedIcon(portCallItem)}
                                                {getMoveIcon(portCallItem, () => onShowMovePortCallPopup(portCallItem))}
                                                {getMovedToIcon(portCallItem)}
                                            </IconWrapper>
                                        </span>
                                        {isMovePortCallPopupOpen(portCallItem) && (
                                            <MoveEventsPopup
                                                node={portCallItem}
                                                onCancelMoveBtn={onCancelPortCallMoveBtn}
                                                onMoveBtn={onPortCallMoveBtn}
                                                onOkMoveBtn={onOkPortCallMoveBtn}
                                            />
                                        )}
                                    </div>
                                    {portCallItem &&
                                        portCallItem?.berthCallArray &&
                                        portCallItem.berthCallArray.map((berthCallItem: BerthCall) => {
                                            return (
                                                <NodeWrapper key={`b-` + berthCallItem.berthCallId}>
                                                    <div
                                                        className={`rotation-node berth-node 
                                            ${isRotationIdEqualCss(selectedNode, portCallItem.id, berthCallItem.id, null, null)}
                                            ${getMovedToOnClickCss(berthCallItem)}
                                            ${getMovedToCss(berthCallItem)}
                                            // ${isManualBerth(berthCallItem)}
                                            `}
                                                        onClick={onSelectedRotationNode(berthCallItem)}
                                                    >
                                                        <span className="rotation-name">
                                                            <span>{getBerthCallName(berthCallItem)}</span>
                                                            <IconWrapper>
                                                                {getDeletedMovedIcon(berthCallItem)}
                                                                {getEditCompletedIcon(berthCallItem)}
                                                                {getMoveIcon(berthCallItem, () => onShowMoveBerthCallPopup(berthCallItem))}
                                                                {getMovedToIcon(berthCallItem)}
                                                            </IconWrapper>
                                                        </span>
                                                        {isMoveBerthCallPopupOpen(berthCallItem) && (
                                                            <MoveEventsPopup
                                                                node={berthCallItem}
                                                                onCancelMoveBtn={onCancelBerthCallMoveBtn}
                                                                onMoveBtn={onBerthCallMoveBtn}
                                                                onOkMoveBtn={onOkBerthCallMoveBtn}
                                                            />
                                                        )}
                                                        {berthCallItem.cargoOperationArray.map((cargoOperationItem: CargoOperation) => {
                                                            return (
                                                                <NodeWrapper key={`c-` + cargoOperationItem.cargoOperationId}>
                                                                    <div
                                                                        className={`rotation-node cargo-node 
                                                        ${isRotationIdEqualCss(
                                                            selectedNode,
                                                            portCallItem.id,
                                                            getBcNumber(berthCallItem),
                                                            getCoNumber(cargoOperationItem),
                                                            null
                                                        )}
                                                        ${getMovedToOnClickCss(cargoOperationItem)}
                                                        ${getMovedToCss(cargoOperationItem)}
                                                    `}
                                                                        onClick={onSelectedRotationNode(cargoOperationItem)}
                                                                    >
                                                                        <span className="rotation-name" title={cargoOperationItem.name}>
                                                                            {getCargoOpName(cargoOperationItem)}{' '}
                                                                        </span>
                                                                        <IconWrapper className="rotation-name-icons">
                                                                            {getDeletedMovedIcon(cargoOperationItem)}
                                                                            {getEditCompletedIcon(cargoOperationItem)}
                                                                            {getMoveIcon(cargoOperationItem, () =>
                                                                                onShowMoveCargoPopup(cargoOperationItem)
                                                                            )}
                                                                            {getMovedToIcon(cargoOperationItem)}
                                                                        </IconWrapper>
                                                                        {isMoveCargoPopupOpen(cargoOperationItem) && (
                                                                            <MoveEventsPopup
                                                                                node={cargoOperationItem}
                                                                                onCancelMoveBtn={onCancelCargoMoveBtn}
                                                                                onMoveBtn={onCargoMoveBtn}
                                                                                onOkMoveBtn={onOkCargoMoveBtn}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </NodeWrapper>
                                                            );
                                                        })}
                                                    </div>
                                                </NodeWrapper>
                                            );
                                        })}
                                </NodeWrapper>
                            );
                        })}
                </div>
            </Wrapper>
        );
    };

    return <>{createRotation()}</>;
};

export default RotationTreeComponent;
